import { AutoSaveStatus, useAutoSaveStatus } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ConfigOptionGroupChangeType, Maybe } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfigurationTrueUpFormInput } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/configurationTrueUpWorkflow";
import {
  hasToggle,
  isProductOptionGroup,
  SelectOptionsFormInput,
  SelectOptionsRowsInput,
  selectOptionsRowsMapToForm,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils";
import { SelectOptionsUtils_OptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, idsOf } from "src/utils";
import {
  DiscontinuedOptionsStep_OptionGroupChangeFragment,
  useDiscontinuedOptionsStep_SaveCheckoutConfigOptionsMutation,
} from "./2.DiscontinuedOptionsStep.generated";

/** The step's rows form input: the groups and `result`'s saved options, plus which original options the user dropped. */
export type DiscontinuedOptionsRowsInput = SelectOptionsRowsInput & { droppedOptionIds: Maybe<string[]> };

/**
 * Saves the selection onto the Draft `result` config, which flags the change event to re-derive on the next save, and
 * records which original options the user dropped. Shared by the step's cards and the Right Side pane: both re-map from
 * what this writes to `formState`.
 */
export function useSaveDiscontinuedOptions(formState: ObjectState<ConfigurationTrueUpFormInput>) {
  const { projectId } = useRouteParams<ProjectParams>();
  const [saveOptions] = useDiscontinuedOptionsStep_SaveCheckoutConfigOptionsMutation();
  const { status, triggerAutoSave, resolveAutoSave } = useAutoSaveStatus();

  async function save(optionIds: string[], droppedOptionIds: string[]): Promise<void> {
    triggerAutoSave();
    try {
      const resultId = formState.configChange.resultId.value ?? fail("Change event has no result config");
      const { data } = await saveOptions({ variables: { input: { id: resultId, optionIds }, structureId: projectId } });
      const result = data?.saveCheckoutConfig.checkoutConfig ?? fail("Save returned no result config");
      formState.configChange.optionIds.set(idsOf(result.config.options, "readyPlanOption"));
      formState.configChange.droppedOptionIds.set(droppedOptionIds);
      // Disables the next steps until the user clicks Continue (or Save & Exit)
      formState.needsReconcile.set(true);
      resolveAutoSave();
    } catch (e) {
      // If something went wrong on backend, the last saved selection stays the truth
      resolveAutoSave(e);
    }
  }

  return { save, saving: status === AutoSaveStatus.SAVING };
}

/** A change whose original selection the new version dropped (`REMOVE`) or swapped for a default (`CHANGE`). */
export type OptionGroupChangeRow = {
  change: DiscontinuedOptionsStep_OptionGroupChangeFragment;
  /** The original selection's `ReadyPlanOptionGroup` id. */
  rpogId: string;
  /** The original selection's `ReadyPlanOption` id. */
  rpoId: string;
  /** Set on a dependent: the row whose option it requires, and that it is kept or removed along with. */
  prerequisite?: OptionGroupChangeRow;
};

/**
 * One row per change that has an original selection; ADD rows have nothing to keep, so they are skipped.
 *
 * A row is a dependent when its original option required another row's original option, per `originalRpov`'s prerequisites
 * (the original version's prerequisites, since the new one may have deactivated it), or its group required another
 * row's group, which only a `REMOVE` leaves empty.
 */
export function createDiscontinuedOptionsRows(
  changes: DiscontinuedOptionsStep_OptionGroupChangeFragment[],
): OptionGroupChangeRow[] {
  const rows = changes
    .filter((change) => !!change.originalOption)
    .map((change) => ({ change, rpogId: change.optionGroup.id, rpoId: change.originalOption!.readyPlanOption.id }));
  return rows.map((row) => {
    const prereqIds = idsOf(row.change.originalRpov?.optionPrerequisites ?? []);
    const groupPrereqIds = idsOf(row.change.originalOption!.readyPlanOption.optionGroup.groupPrerequisites);
    const prerequisite = rows.find(
      (other) =>
        prereqIds.includes(other.rpoId) ||
        (other.change.changeType === ConfigOptionGroupChangeType.Remove && groupPrereqIds.includes(other.rpogId)),
    );
    return { ...row, prerequisite };
  });
}

/**
 * The changed groups plus their parent/child groups, in `original`'s checkout order so a group `result` dropped still
 * lands in its slot. Each row's original option, at the price the customer reserved it for, is upserted into its
 * `result` group (or `original`'s, when `result` has none).
 */
export function createDiscontinuedOptionGroups(
  rows: OptionGroupChangeRow[],
  resultGroups: SelectOptionsUtils_OptionGroupFragment[],
  originalGroups: SelectOptionsUtils_OptionGroupFragment[],
): SelectOptionsUtils_OptionGroupFragment[] {
  const resultByRpogId = resultGroups.keyBy((ccfgog) => ccfgog.group.id);
  const originalOptionByRpoId = originalGroups.flatMap((ccfgog) => ccfgog.options).keyBy((ccfgpo) => ccfgpo.option.id);
  const rowsByRpogId = rows.groupBy((row) => row.rpogId);
  const changedIds = new Set(rows.map((row) => row.rpogId));
  const keptIds = new Set(
    [...resultGroups, ...originalGroups]
      .filter((ccfgog) => changedIds.has(ccfgog.group.id))
      .flatMap((ccfgog) => [ccfgog.group.id, ccfgog.parentGroup?.id, ccfgog.childGroup?.id])
      .compact(),
  );
  return originalGroups
    .filter((originalGroup) => keptIds.has(originalGroup.group.id))
    .map((originalGroup) => {
      const group = resultByRpogId[originalGroup.group.id] ?? originalGroup;
      const options = (rowsByRpogId[group.group.id] ?? [])
        .map((row) => {
          const potential = originalOptionByRpoId[row.rpoId];
          if (!potential) return undefined;
          return {
            ...potential,
            option: { ...potential.option, name: row.change.originalRpov?.name ?? potential.option.name },
            // The server says the original option can't be selected anymore (e.g. it was discontinued), which would hide
            // its card or disable its checkbox. We allow it so the user can keep it or let it go.
            // Except for a checkbox that depends on another row (e.g. "Walk-in Closet" requires "Add 4th Bedroom"):
            // it stays disabled, with a "Requires …" tooltip, and follows that row instead. A pick-one dependent
            // (e.g. an exterior scheme that requires the elevation) stays selectable.
            canBeSelected:
              row.prerequisite && hasDroppedToggle(group, rows)
                ? {
                    allowed: false,
                    disabledReasons: [{ message: `Requires ‘${row.prerequisite.change.originalRpov?.name}’` }],
                  }
                : { allowed: true, disabledReasons: [] },
          };
        })
        .compact();
      const upsertedIds = new Set(options.map((ccfgpo) => ccfgpo.option.id));
      return {
        ...group,
        // So `showGroup` keeps a dependent's group and one `result` dropped
        canBeSelected: { ...group.canBeSelected, allowed: true },
        options: [...group.options.filter((ccfgpo) => !upsertedIds.has(ccfgpo.option.id)), ...options],
      };
    });
}

/**
 * Whether the group's card shows a checkbox. On top of the usual `hasToggle` groups, a pick-one product group gets one
 * when the new version removed its option without a replacement, so the user can uncheck it to let the option go.
 */
export function hasDroppedToggle(group: SelectOptionsUtils_OptionGroupFragment, rows: OptionGroupChangeRow[]): boolean {
  if (hasToggle(group)) return true;
  if (group.parentGroup || !isProductOptionGroup(group)) return false;
  return rows.some(
    (row) => row.rpogId === group.group.id && row.change.changeType === ConfigOptionGroupChangeType.Remove,
  );
}

/** The original options the cards drop: a row is kept only while its group is checked on its original option. */
export function droppedOptionIdsOf(rows: OptionGroupChangeRow[], formRows: SelectOptionsFormInput[]): string[] {
  const formRowByRpogId = formRows.keyBy((row) => row.groupId);
  return rows
    .filter((row) => {
      const formRow = formRowByRpogId[row.rpogId];
      return !formRow?.selected || formRow.optionId !== row.rpoId;
    })
    .map((row) => row.rpoId);
}

/**
 * The cards' picks from `result`'s saved options and the kept original options:
 * - a kept row picks its original option in place of what `result` has in its group;
 * - a row that isn't kept picks what `result` has: its replacement or alternative. A group with a dropped checkbox
 *   and nothing in `result` shows its original option unchecked.
 */
export function mapDiscontinuedOptionsRowsToForm(
  input: SelectOptionsRowsInput,
  rows: OptionGroupChangeRow[],
  droppedOptionIds: Maybe<string[]>,
): { rows: SelectOptionsFormInput[] } {
  const droppedIds = new Set(droppedOptionIds);
  const groupByRpogId = input.groups.keyBy((ccfgog) => ccfgog.group.id);
  const kept = rows.filter((row) => !droppedIds.has(row.rpoId));
  // A kept row's original replaces whatever its group has in `result`: the new version's option, or an alternative
  // the user picked before keeping it again (e.g. from the pane's "Clear all changes")
  const replacedIds = new Set(
    kept.flatMap((row) => [
      row.change.targetOption?.readyPlanOption.id,
      ...(groupByRpogId[row.rpogId]?.options.map((ccfgpo) => ccfgpo.option.id) ?? []),
    ]),
  );
  const optionIds = [...(input.optionIds ?? []).filter((id) => !replacedIds.has(id)), ...kept.map((row) => row.rpoId)];
  const optionIdSet = new Set(optionIds);
  const droppedByRpogId = rows.filter((row) => droppedIds.has(row.rpoId)).keyBy((row) => row.rpogId);
  const formRows = selectOptionsRowsMapToForm({ ...input, optionIds }).rows.map((formRow) => {
    const dropped = droppedByRpogId[formRow.groupId];
    const group = groupByRpogId[formRow.groupId];
    const empty = !group?.options.some((ccfgpo) => optionIdSet.has(ccfgpo.option.id));
    return dropped && group && empty && hasDroppedToggle(group, rows)
      ? { ...formRow, selected: false, optionId: dropped.rpoId }
      : formRow;
  });
  return { rows: formRows };
}

/**
 * The dropped options after keeping the row if it was dropped, or dropping it if it was kept. Its dependents (and
 * theirs) follow it, like they do on the cards.
 */
export function toggleDiscontinuedOptionsKept(
  rows: OptionGroupChangeRow[],
  droppedIds: string[],
  row: OptionGroupChangeRow,
): string[] {
  const moved = [row];
  const queue = [row];
  while (queue.nonEmpty) {
    const prerequisite = queue.shift()!;
    rows
      .filter((other) => other.prerequisite?.rpoId === prerequisite.rpoId && !moved.includes(other))
      .forEach((other) => {
        moved.push(other);
        queue.push(other);
      });
  }
  const movedIds = moved.map((r) => r.rpoId);
  return droppedIds.includes(row.rpoId) ? droppedIds.difference(movedIds) : droppedIds.concat(movedIds).unique();
}

/**
 * The option ids to save onto `result`:
 * - options from groups this step doesn't show stay as they are;
 * - a child group's option only counts while its parent group is selected;
 * - a kept discontinued option can't be saved on `result`, so its group gets the new version's option instead (or
 *   nothing, if the new version removed the group's option).
 */
export function mapDiscontinuedOptionsRowsToInput(
  formState: ObjectState<ConfigurationTrueUpFormInput>,
  formRows: SelectOptionsFormInput[],
  groups: SelectOptionsUtils_OptionGroupFragment[],
  rows: OptionGroupChangeRow[],
): string[] {
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const formRowByRpogId = formRows.keyBy((row) => row.groupId);
  const rowByRpoId = rows.keyBy((row) => row.rpoId);
  const loadedOptionIds = new Set(groups.flatMap((ccfgog) => ccfgog.options.map((ccfgpo) => ccfgpo.option.id)));
  const picked = formRows
    .filter((row) => {
      if (!row.selected || !row.optionId) return false;
      const parentId = groupByRpogId[row.groupId]?.parentGroup?.id;
      return !parentId || !!formRowByRpogId[parentId]?.selected;
    })
    // TODO(PINT-305): Send kept options as custom options (`customOptionSourceIds`) once the BE handles them, along
    // with their dependents that are kept too
    .map((row) => {
      const kept = rowByRpoId[row.optionId!];
      return kept ? kept.change.targetOption?.readyPlanOption.id : row.optionId;
    })
    .compact();
  const unloaded = (formState.configChange.optionIds.value ?? []).filter((id) => !loadedOptionIds.has(id));
  return [...unloaded, ...picked].unique();
}
